/**
 * Every event type the server records.
 *
 * One list, so the labels a screen shows are held to it by type and by test:
 * a new event cannot reach the audit trail as raw text.
 *
 * @module server/audit-events
 */

import { AUTH_DENIAL_REASONS, INVITE_CLAIM_DENIAL_REASONS } from "@/server/auth/denial-reasons";

type LoginDenial = Exclude<
  (typeof INVITE_CLAIM_DENIAL_REASONS)[number] | (typeof AUTH_DENIAL_REASONS)[number],
  "platform-account"
>;

/** Every way a sign-in is refused, each recorded as `login.<reason>`. A platform account's refusal only the server log holds. */
const LOGIN_DENIALS = [...INVITE_CLAIM_DENIAL_REASONS, ...AUTH_DENIAL_REASONS].filter(
  (reason): reason is LoginDenial => reason !== "platform-account",
);

export const AUDIT_EVENT_TYPES = [
  "assessment.override-assessments-added",
  "assessment.override-assessments-removed",
  "assessment.override-created",
  "assessment.override-reverted",
  "assessment.override-updated",
  "assignment.created",
  "edit-lease.acquired",
  "edit-lease.disconnected",
  "edit-lease.released",
  "final-sign-off.recorded",
  "impersonation.started",
  "impersonation.stopped",
  "invitation.created",
  "invitation.reissued",
  "login.identity-bound",
  "login.success",
  "module-owner.assigned",
  "module.created",
  "module.reviewed",
  "module.saved",
  "module.submitted",
  "programme-responsibility.assigned",
  "programme.amendment-opened",
  "programme.approval-candidate-created",
  "programme.approved",
  "programme.change-case-approved",
  "programme.change-case-discarded",
  "programme.change-case-identity-impact-decided",
  "programme.change-case-internal-review-completed",
  "programme.change-case-reviewed",
  "programme.change-case-submitted",
  "programme.change-case-updated",
  "programme.change-case-withdrawn",
  "programme.change-team-confirmed",
  "programme.change-team-reset",
  "programme.create",
  "programme.created",
  "programme.curriculum-migrated",
  "programme.delivery-status-changed",
  "programme.end-of-life-set",
  "programme.export",
  "programme.export-previewed",
  "programme.exported",
  "programme.governance-finding-created",
  "programme.governance-finding-resolved",
  "programme.internal-review-skipped",
  "programme.publication.published",
  "programme.publication.withdrawn",
  "programme.readiness-overridden",
  "programme.replacement-created",
  "programme.returned-to-draft",
  "programme.review-recorded",
  "programme.review-round-opened",
  "programme.revised",
  "programme.revised-from-snapshot",
  "programme.revision-applicability-created",
  "programme.signed-off",
  "programme.snapshot-created",
  "programme.state_changed",
  "programme.updated",
  "programme.visibility-changed",
  "programme.workflow-transitioned",
  "role.changed",
  "settings.access-policy.updated",
  "settings.provider.updated",
  "settings.updated",
  "snapshot.restored",
  "support-access.enabled",
  "support-access.revoked",
  "support-access.used",
  "tenant-capabilities.changed",
  "tenant.created",
  "tenant.entitlement.decided",
  "user.activated",
  "user.deactivated",
  "user.email-changed",
  "user.invited",
  "workflow.transitioned",
  ...LOGIN_DENIALS.map((reason) => `login.${reason}` as const),
] as const;

export type AuditEventType = (typeof AUDIT_EVENT_TYPES)[number];
